import {AVAILABLE_OR_NOT, PAYMENT_TYPE} from '../../CodeMaster'
import FacilityForReservation from '../facilities/FacilityForReservation'
import {Nullable} from '../../app/TypeHelpers'
import ReserveFacilityPaymentModel from './ReserveFacilityPaymentModel'
import {ReserveAvailableStatusOvernight} from './ReserveAvailabilityOvernight'
import {ReserveAvailableStatusDuration} from './ReserveAvailabilityDuration'
import {SelectedUser} from '../../__NoAutomatedTests/components/common/resident/NatResidentSelectedUser'

export interface NewOvernightRentalFacilityReservation {
    startDate: string
    stayCount: number
    reservationTitle: string
    sharedUseRoomNumberList: RoomNumberView[]
    options: ReservationOption[]

    paymentInformation: PaymentInformationForReservation
    reserveType: Nullable<string>
}

export interface NewDurationRentalFacilityReservation {
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    reservationTitle: string
    sharedUseRoomNumberList: RoomNumberView[]
    options: ReservationOption[]

    paymentInformation: PaymentInformationForReservation
    reserveType: Nullable<string>
}

export interface FacilityReservation {
    reserveId: number
    facilityName: string
    stayAvailability: string
    reserveType: string
    reserveStatus: string
    startDate: string
    startTime: Nullable<string>
    endDate: Nullable<string>
    endTime: Nullable<string>
    stayCount: Nullable<number>
    reservationTitle: string
    sharedUseRoomNumberList: SelectedUser[]
    options: ReservationOption[]

    paymentInformation: PaymentInformationForReservation

    version: number
}

export interface PaymentItem {
    id: Nullable<number>
    article: string
    quantityWithUnit: Nullable<string>
    paymentType: PAYMENT_TYPE
    charge: Nullable<number>
    displayOrder: number
}

export interface PaymentItemForReservation {
    id: Nullable<number>
    article: string
    quantityWithUnit: Nullable<string>
    paymentType: Nullable<string>
    charge: Nullable<number>
}

export interface PaymentInformation {
    items: PaymentItem[]
    totalCharge: Nullable<number>
}

export interface PaymentInformationForReservation {
    items: PaymentItemForReservation[]
    totalCharge: Nullable<number>
}

export interface ReserveFacilityModelDataSetObserver {
    onDataSetStateChange: (completeToCreate: boolean) => void
}

export interface ReserveFacilityModelNewReservationObserver {
    onBuildOvernightRentalFacilityReservation: (
        newReservation: NewOvernightRentalFacilityReservation,
        facilityData: FacilityForReservation,
    ) => void
    onBuildDurationRentalFacilityReservation: (
        newReservation: NewDurationRentalFacilityReservation,
        facilityData: FacilityForReservation,
    ) => void
}

export interface ReserveFacilityModelUpdateReservationObserver {
    onBuildUpdateRentalFacilityReservation: (
        updateReservation: FacilityReservation,
        facilityData: FacilityForReservation,
    ) => void
}

export interface ReserveFacilityModelPaymentObserver {
    onChangePayment: (paymentProps: PaymentInformation) => void
}

export interface RoomNumberView {
    ridgeCd: string
    ridgeName: string
    roomNumber: string
}

export interface ReservationOption {
    optionId: number
    quantity: number
    sortOrder: number
}

export class ReserveFacilityModel implements ReserveFacilityModelPaymentObserver {
    protected dataSetObservers: ReserveFacilityModelDataSetObserver[] = []
    protected newReservationObservers: ReserveFacilityModelNewReservationObserver[] = []
    protected updateReservationObservers: ReserveFacilityModelUpdateReservationObserver[] = []
    private paymentModel: ReserveFacilityPaymentModel
    private stayAvailable: Nullable<boolean> = null
    private facilityData: Nullable<FacilityForReservation> = null

    private reservationOptions: Nullable<ReservationOption[]> = null

    private startDate: Nullable<string> = null
    private startTime: Nullable<string> = null
    private endDate: Nullable<string> = null
    private endTime: Nullable<string> = null
    private stayCount: Nullable<number> = null
    private reservationTitle: string = ''

    private paymentInformation: Nullable<PaymentInformationForReservation> = null
    private calculatePaymentComplete: boolean = false
    private availableListOvernight: Nullable<ReserveAvailableStatusOvernight[]> = null
    private availableListDuration: Nullable<ReserveAvailableStatusDuration[]> = null
    private selectedUsers: SelectedUser[] = []

    private reservationId: Nullable<number> = null
    private facilityReserveDataVersion: Nullable<number> = null
    private reserveType: Nullable<string> = null
    private reserveStatus: Nullable<string> = null

    constructor(reservePaymentModel: ReserveFacilityPaymentModel) {
        this.paymentModel = reservePaymentModel
        this.paymentModel.addPaymentInformationObserver(this)
    }

    reset(): ReserveFacilityModel {
        this.paymentModel.reset()
        this.stayAvailable = null
        this.reserveType = null
        this.reserveStatus = null
        this.reservationId = null
        this.reservationOptions = null
        this.startDate = null
        this.startTime = null
        this.endDate = null
        this.endTime = null
        this.stayCount = null
        this.reservationTitle = ''
        this.selectedUsers = []
        this.calculatePaymentComplete = false
        this.availableListOvernight = null
        this.availableListDuration = null
        this.dataSetObservers = []
        this.newReservationObservers = []
        this.facilityReserveDataVersion = null

        this.paymentModel.addPaymentInformationObserver(this)
        return this
    }

    addDataSetStateObserver(
        observer: ReserveFacilityModelDataSetObserver,
    ): ReserveFacilityModel {
        this.dataSetObservers = [...this.dataSetObservers, observer]
        return this
    }

    addNewReservationObserver(
        observer: ReserveFacilityModelNewReservationObserver,
    ): ReserveFacilityModel {
        this.newReservationObservers = [...this.newReservationObservers, observer]
        return this
    }

    addUpdateRentalFacilityReservation(
        observer: ReserveFacilityModelUpdateReservationObserver,
    ): ReserveFacilityModel {
        this.updateReservationObservers = [...this.updateReservationObservers, observer]
        return this
    }

    addPaymentInformationObserver(
        observer: ReserveFacilityModelPaymentObserver,
    ): ReserveFacilityModel {
        this.paymentModel.addPaymentInformationObserver(observer)
        return this
    }

    withFacilityData(
        facilityData: FacilityForReservation,
    ): ReserveFacilityModel {
        this.facilityData = facilityData
        this.stayAvailable = facilityData.stayAvailability
        this.paymentModel.withStayAvailable(this.stayAvailable)

        if (this.stayAvailable) {
            this.paymentModel.withOvernightFacilityData(facilityData.overnightFacilitySetting)
        } else {
            this.paymentModel.withDurationFacilityData(facilityData.durationFacilitySetting)
        }

        this.paymentModel.withFacilityOptionSettings(facilityData.facilityOptions)
        return this
    }

    withHolidays(
        holidays: Nullable<string[]>,
    ): ReserveFacilityModel {
        this.paymentModel.withHolidays(holidays)
        return this
    }

    withOvernightReserveAvailableData(
        availableList: ReserveAvailableStatusOvernight[],
    ): ReserveFacilityModel {
        this.availableListOvernight = availableList
        this.notifyChangeToObservers()
        return this
    }

    withDurationReserveAvailableData(
        availableList: ReserveAvailableStatusDuration[],
    ): ReserveFacilityModel {
        this.availableListDuration = availableList
        this.notifyChangeToObservers()
        return this
    }

    withReservationOptions(options: ReservationOption[]): ReserveFacilityModel {
        this.reservationOptions = options
        this.notifyChangeToObservers()

        this.paymentModel.withReservationOptions(options)
        return this
    }

    withDurationReservationData(
        startDate: string,
        startTime: Nullable<string>,
        endDate: Nullable<string>,
        endTime: Nullable<string>,
    ): ReserveFacilityModel {
        this.startDate = startDate
        this.startTime = startTime
        this.endDate = endDate
        this.endTime = endTime
        this.notifyChangeToObservers()
        this.paymentModel.withDurationReservationData(
            this.startDate = startDate,
            this.startTime = startTime,
            this.endDate = endDate,
            this.endTime = endTime,
        )
        return this
    }

    withOvernightReservationData(startDate: Nullable<string>, stayCount: number): ReserveFacilityModel {
        this.startDate = startDate
        this.stayCount = stayCount
        this.notifyChangeToObservers()

        this.paymentModel.withOvernightReservationData(
            this.startDate = startDate,
            this.stayCount = stayCount,
        )
        return this
    }

    withReservationTitle(reservationTitle: string): ReserveFacilityModel {
        this.reservationTitle = reservationTitle

        return this
    }

    withSelectedUserList(selectedUsers: SelectedUser[]): ReserveFacilityModel {
        this.selectedUsers = selectedUsers
        return this
    }

    withReservationId(reservationId: Nullable<number>): ReserveFacilityModel {
        this.reservationId = reservationId
        return this
    }

    withVersion(version: Nullable<number>): ReserveFacilityModel {
        this.facilityReserveDataVersion = version
        return this
    }

    withReserveType(reserveType: Nullable<string>): ReserveFacilityModel {
        this.reserveType = reserveType
        return this
    }

    withReserveStatus(reserveStatus: Nullable<string>): ReserveFacilityModel {
        this.reserveStatus = reserveStatus
        return this
    }

    build(): void {
        if (this.stayAvailable === null) {
            throw Error('this.stayAvailable cannot be null')
        }

        if (this.reservationId === null) {
            if (this.stayAvailable) {
                this.newReservationObservers
                    .forEach(observer => {
                        return observer.onBuildOvernightRentalFacilityReservation(
                            {
                                startDate: this.startDate as string,
                                stayCount: this.stayCount as number,
                                reservationTitle: this.reservationTitle,
                                sharedUseRoomNumberList: this.selectedUsers,
                                options: this.reservationOptions as ReservationOption[],

                                paymentInformation: this.paymentInformation as PaymentInformationForReservation,
                                reserveType: this.reserveType as string,
                            },
                            {
                                ...this.facilityData,
                            } as FacilityForReservation,
                        )
                    })
            } else {
                this.newReservationObservers
                    .forEach(observer => {
                        return observer.onBuildDurationRentalFacilityReservation({
                                startDate: this.startDate as string,
                                startTime: this.startTime as string,
                                endDate: this.endDate as string,
                                endTime: this.endTime as string,
                                reservationTitle: this.reservationTitle,
                                sharedUseRoomNumberList: this.selectedUsers,
                                options: this.reservationOptions as ReservationOption[],

                                paymentInformation: this.paymentInformation as PaymentInformationForReservation,
                                reserveType: this.reserveType as string,
                            },
                            {
                                ...this.facilityData,
                            } as FacilityForReservation,
                        )

                    })
            }
        } else {
            let stayAvailable: string = AVAILABLE_OR_NOT.UNAVAILABLE.value
            if (this.stayAvailable) {
                stayAvailable = AVAILABLE_OR_NOT.AVAILABLE.value
            }
            let facilityName: string = ''
            if (this.facilityData !== null) {
                facilityName = this.facilityData.facilityName
            }
            this.updateReservationObservers
                .forEach(observer => {
                    observer.onBuildUpdateRentalFacilityReservation({
                            reserveId: this.reservationId as number,
                            facilityName: facilityName,
                            stayAvailability: stayAvailable,
                            reserveType: this.reserveType as string,
                            reserveStatus: this.reserveStatus as string,
                            startDate: this.startDate as string,
                            startTime: this.startTime as string,
                            endDate: this.endDate as string,
                            endTime: this.endTime as string,
                            stayCount: this.stayCount as number,
                            reservationTitle: this.reservationTitle,
                            sharedUseRoomNumberList: this.selectedUsers,
                            options: this.reservationOptions as ReservationOption[],

                            paymentInformation: this.paymentInformation as PaymentInformationForReservation,
                            version: this.facilityReserveDataVersion as number,
                        },
                        {
                            ...this.facilityData,
                        } as FacilityForReservation,
                    )
                })
        }
    }

    onChangePayment(paymentProps: PaymentInformation): void {
        const {items, totalCharge} = paymentProps
        this.paymentInformation = {
            items: items.map(item => {
                const {paymentType, displayOrder, ...rest} = item
                return {
                    ...rest,
                    paymentType: paymentType.value,
                }
            }),
            totalCharge: totalCharge,
        }
        this.calculatePaymentComplete = (paymentProps.totalCharge !== null)
        this.notifyChangeToObservers()
    }

    protected notifyChangeToObservers(): void {
        this.dataSetObservers.forEach(
            (observer) => observer.onDataSetStateChange(this.checkDataSetCompleted()),
        )
    }

    protected checkDataSetCompleted(): boolean {
        if (this.stayAvailable === null) {
            return false
        }

        if (this.stayAvailable) {
            return this.checkDataSetCompletedOvernight()
        } else {
            return this.checkDataSetCompletedDuration()
        }
    }

    private checkDataSetCompletedDuration(): boolean {
        if (!this.calculatePaymentComplete) {
            return false
        }

        if (!this.startDate) {
            return false
        }

        if (!this.startTime) {
            return false
        }

        if (!this.endDate) {
            return false
        }

        // noinspection RedundantIfStatementJS
        if (!this.endTime) {
            return false
        }
        return true
    }

    private checkDataSetCompletedOvernight(): boolean {
        if (!this.calculatePaymentComplete) {
            return false
        }

        if (!this.startDate) {
            return false
        }

        // noinspection RedundantIfStatementJS
        if (!this.stayCount) {
            return false
        }

        return true
    }
}
